import FetchEventSource from 'fetch-event-source';
import { getToken } from '@/utils/auth';

const baseURL = process.env.VUE_APP_BASE_API + '/financeapi/sse';

const CHANNEL_PREFIX = 'finance:sse:channel';
const LOCK_PREFIX = 'finance:sse:leader';
const FALLBACK_LOCK_PREFIX = 'finance:sse:lock';
const DEFAULT_MAX_RETRIES = Infinity;
const DEFAULT_MAX_RETRY_DELAY = 30000;
const RETRY_BASE_DELAY = 1000;
const LOCK_RETRY_INTERVAL = 2500;
const LEADER_TTL = 12000;
const HEARTBEAT_INTERVAL = 4000;
const LEADER_CHECK_INTERVAL = 5000;

function safeParse (value) {
  if (!value) {
    return null;
  }
  try {
    return JSON.parse(value);
  } catch (e) {
    return null;
  }
}

function createTabId () {
  return 'tab_' + Date.now() + '_' + Math.random().toString(36).slice(2, 10);
}

function isLocksSupported () {
  return typeof navigator !== 'undefined' && !!navigator.locks && typeof navigator.locks.request === 'function';
}

function asTypeKey (type) {
  return String(type);
}

function asMessageTypeKey (messageType) {
  return String(messageType || '').trim().toUpperCase();
}

const LEGACY_TYPE_MESSAGE_MAP = {
  0: 'GENERAL_NOTIFICATION',
  1: 'VOUCHER_GENERATE'
};

function getAccountScopeKey () {
  const user = safeParse(localStorage.getItem('loginUser')) || {};
  const account = user.account ? String(user.account).trim() : '';
  const jobNumber = user.jobNumber ? String(user.jobNumber).trim() : '';
  if (account && jobNumber) {
    return account + ':' + jobNumber;
  }
  if (account) {
    return account;
  }
  if (jobNumber) {
    return 'cashier:' + jobNumber;
  }
  return 'default-account';
}

class LeaderElector {
  constructor (mode) {
    this.mode = mode;
    this.isLeader = false;
    this.listeners = new Set();
  }

  onLeaderChanged (listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  emitLeaderChanged (isLeader) {
    if (this.isLeader === isLeader) {
      return;
    }
    this.isLeader = isLeader;
    this.listeners.forEach(listener => listener({ isLeader, mode: this.mode }));
  }

  acquire () {}

  release () {}
}

class NavigatorLocksElector extends LeaderElector {
  constructor ({ lockName, retryInterval = LOCK_RETRY_INTERVAL }) {
    super('locks');
    this.lockName = lockName;
    this.retryInterval = retryInterval;
    this.stopped = true;
    this.acquireTimer = null;
    this.acquireInFlight = false;
    this.holdPromise = null;
    this.resolveHold = null;
  }

  acquire () {
    this.stopped = false;
    this.scheduleAcquire(0);
  }

  scheduleAcquire (delay) {
    if (this.acquireTimer) {
      clearTimeout(this.acquireTimer);
      this.acquireTimer = null;
    }
    this.acquireTimer = setTimeout(() => {
      this.acquireTimer = null;
      this.tryAcquire();
    }, delay);
  }

  async tryAcquire () {
    if (this.stopped || this.acquireInFlight) {
      return;
    }

    this.acquireInFlight = true;
    try {
      await navigator.locks.request(this.lockName, {
        mode: 'exclusive',
        ifAvailable: true
      }, async lock => {
        if (this.stopped) {
          return;
        }
        if (!lock) {
          this.emitLeaderChanged(false);
          return;
        }

        this.holdPromise = new Promise(resolve => {
          this.resolveHold = resolve;
        });

        this.emitLeaderChanged(true);
        await this.holdPromise;
      });
    } catch (error) {
      this.emitLeaderChanged(false);
    } finally {
      this.acquireInFlight = false;
      if (!this.stopped && !this.isLeader) {
        this.scheduleAcquire(this.retryInterval);
      }
    }
  }

  release () {
    this.stopped = true;

    if (this.acquireTimer) {
      clearTimeout(this.acquireTimer);
      this.acquireTimer = null;
    }

    if (this.resolveHold) {
      const resolve = this.resolveHold;
      this.resolveHold = null;
      this.holdPromise = null;
      resolve();
    }

    this.emitLeaderChanged(false);
  }
}

class LocalStorageElector extends LeaderElector {
  constructor ({ tabId, lockKey }) {
    super('localStorage');
    this.tabId = tabId;
    this.lockKey = lockKey;
    this.started = false;
    this.heartbeatTimer = null;
    this.leaderCheckTimer = null;
  }

  acquire () {
    if (!this.started) {
      this.started = true;
      this.startHeartbeat();
      this.startLeaderCheck();
    }
    this.tryBecomeLeader();
  }

  release () {
    this.started = false;
    this.stopHeartbeat();
    this.stopLeaderCheck();

    if (this.isLeader) {
      localStorage.removeItem(this.lockKey);
    }
    this.emitLeaderChanged(false);
  }

  getLockInfo () {
    return safeParse(localStorage.getItem(this.lockKey));
  }

  isLockAlive (lock) {
    if (!lock || !lock.ts || !lock.tabId) {
      return false;
    }
    return Date.now() - Number(lock.ts) < LEADER_TTL;
  }

  writeLock () {
    localStorage.setItem(this.lockKey, JSON.stringify({
      tabId: this.tabId,
      ts: Date.now()
    }));
  }

  tryBecomeLeader () {
    if (!this.started) {
      return;
    }

    const lock = this.getLockInfo();
    if (this.isLockAlive(lock) && lock.tabId !== this.tabId) {
      this.emitLeaderChanged(false);
      return;
    }

    this.writeLock();
    const confirmed = this.getLockInfo();
    this.emitLeaderChanged(!!confirmed && confirmed.tabId === this.tabId);
  }

  startHeartbeat () {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (!this.started || !this.isLeader) {
        return;
      }
      this.writeLock();
    }, HEARTBEAT_INTERVAL);
  }

  stopHeartbeat () {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  startLeaderCheck () {
    this.stopLeaderCheck();
    this.leaderCheckTimer = setInterval(() => {
      if (!this.started) {
        return;
      }

      const lock = this.getLockInfo();
      if (!this.isLockAlive(lock)) {
        this.tryBecomeLeader();
        return;
      }

      if (lock.tabId !== this.tabId && this.isLeader) {
        this.emitLeaderChanged(false);
      }
    }, LEADER_CHECK_INTERVAL);
  }

  stopLeaderCheck () {
    if (this.leaderCheckTimer) {
      clearInterval(this.leaderCheckTimer);
      this.leaderCheckTimer = null;
    }
  }
}

function createLeaderElector ({ accountScope, tabId }) {
  if (isLocksSupported()) {
    return new NavigatorLocksElector({
      lockName: LOCK_PREFIX + ':' + accountScope
    });
  }

  return new LocalStorageElector({
    tabId,
    lockKey: FALLBACK_LOCK_PREFIX + ':' + accountScope
  });
}

class SharedSSEManager {
  constructor () {
    this.tabId = createTabId();
    this.accountScope = '';
    this.isStarted = false;
    this.isLeader = false;
    this.electorMode = 'localStorage';

    this.channelSupported = typeof window !== 'undefined' && typeof window.BroadcastChannel !== 'undefined';
    this.channel = null;
    this.channelName = '';

    this.elector = null;
    this.electorUnsubscribe = null;

    this.eventSource = null;
    this.connectionId = 0;

    this.retryCount = 0;
    this.maxRetries = DEFAULT_MAX_RETRIES;
    this.maxRetryDelay = DEFAULT_MAX_RETRY_DELAY;
    this.reconnectTimer = null;
    this.reconnectAttempt = 0;
    this.reconnectGeneration = 0;
    this.isReconnecting = false;
    this.isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;

    this.messageSubscribers = new Set();
    this.statusSubscribers = new Set();
    this.typeSubscribers = new Map();
    this.messageTypeSubscribers = new Map();

    this.beforeUnloadHandler = this.handleBeforeUnload.bind(this);
    this.onlineHandler = this.handleOnline.bind(this);
    this.offlineHandler = this.handleOffline.bind(this);
  }

  start ({ maxRetries, maxRetryDelay } = {}) {
    if (typeof maxRetries === 'number') {
      this.maxRetries = maxRetries > 0 ? maxRetries : DEFAULT_MAX_RETRIES;
    }
    if (typeof maxRetryDelay === 'number') {
      this.maxRetryDelay = maxRetryDelay;
    }

    const accountScope = getAccountScopeKey();

    if (this.isStarted && this.accountScope === accountScope) {
      return this.getState();
    }

    if (this.isStarted && this.accountScope !== accountScope) {
      this.stop('client-changed');
    }

    this.accountScope = accountScope;
    this.isStarted = true;
    this.resetReconnectController();

    window.addEventListener('beforeunload', this.beforeUnloadHandler);
    window.addEventListener('online', this.onlineHandler);
    window.addEventListener('offline', this.offlineHandler);

    if (!this.channelSupported) {
      this.isLeader = true;
      this.electorMode = 'localStorage';
      this.openSSE();
      return this.getState();
    }

    this.initChannel();
    this.initElector();
    return this.getState();
  }

  stop (reason = 'manual-stop') {
    if (!this.isStarted) {
      return;
    }

    this.isStarted = false;
    this.retryCount = 0;

    if (this.isLeader) {
      this.broadcast({
        kind: 'leader-release',
        mode: this.electorMode
      });
    }

    this.destroyElector();
    this.isLeader = false;
    this.resetReconnectController();
    this.closeSSE();

    this.broadcast({
      kind: 'status',
      status: {
        state: 'closed',
        reason
      }
    });
    this.dispatchStatus({
      state: 'closed',
      reason
    });

    this.destroyChannel();
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    window.removeEventListener('online', this.onlineHandler);
    window.removeEventListener('offline', this.offlineHandler);

    this.accountScope = '';
    this.maxRetries = DEFAULT_MAX_RETRIES;
    this.maxRetryDelay = DEFAULT_MAX_RETRY_DELAY;
  }

  subscribe (handler) {
    this.messageSubscribers.add(handler);
    return () => this.messageSubscribers.delete(handler);
  }

  subscribeByType (type, handler) {
    const key = asTypeKey(type);
    const handlers = this.typeSubscribers.get(key) || new Set();
    handlers.add(handler);
    this.typeSubscribers.set(key, handlers);

    return () => {
      const exists = this.typeSubscribers.get(key);
      if (!exists) {
        return;
      }
      exists.delete(handler);
      if (exists.size === 0) {
        this.typeSubscribers.delete(key);
      }
    };
  }

  subscribeByMessageType (messageType, handler) {
    const key = asMessageTypeKey(messageType);
    const handlers = this.messageTypeSubscribers.get(key) || new Set();
    handlers.add(handler);
    this.messageTypeSubscribers.set(key, handlers);

    return () => {
      const exists = this.messageTypeSubscribers.get(key);
      if (!exists) {
        return;
      }
      exists.delete(handler);
      if (exists.size === 0) {
        this.messageTypeSubscribers.delete(key);
      }
    };
  }

  subscribeStatus (handler) {
    this.statusSubscribers.add(handler);
    return () => this.statusSubscribers.delete(handler);
  }

  getState () {
    return {
      isStarted: this.isStarted,
      isLeader: this.isLeader,
      electorMode: this.electorMode,
      retryCount: this.retryCount,
      reconnectAttempt: this.reconnectAttempt,
      isReconnecting: this.isReconnecting
    };
  }

  initElector () {
    this.destroyElector();
    this.elector = createLeaderElector({
      accountScope: this.accountScope,
      tabId: this.tabId
    });
    this.electorMode = this.elector.mode;

    this.electorUnsubscribe = this.elector.onLeaderChanged(({ isLeader, mode }) => {
      this.handleLeaderChange(isLeader, mode);
    });

    this.elector.acquire();
  }

  destroyElector () {
    if (this.electorUnsubscribe) {
      this.electorUnsubscribe();
      this.electorUnsubscribe = null;
    }

    if (this.elector) {
      this.elector.release();
      this.elector = null;
    }
  }

  handleLeaderChange (isLeader, mode) {
    if (!this.isStarted || this.isLeader === isLeader) {
      return;
    }

    this.isLeader = isLeader;
    this.electorMode = mode || this.electorMode;

    if (this.isLeader) {
      this.resetReconnectController();
      this.broadcast({
        kind: 'leader-active',
        mode: this.electorMode
      });
      this.openSSE();
      return;
    }

    this.resetReconnectController();
    this.closeSSE();
    this.broadcast({
      kind: 'leader-release',
      mode: this.electorMode
    });
  }

  initChannel () {
    this.destroyChannel();
    this.channelName = CHANNEL_PREFIX + ':' + this.accountScope;
    this.channel = new BroadcastChannel(this.channelName);
    this.channel.onmessage = event => this.handleChannelMessage(event.data);
  }

  destroyChannel () {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
    this.channelName = '';
  }

  handleChannelMessage (payload) {
    if (!payload || payload.tabId === this.tabId) {
      return;
    }

    if (payload.kind === 'leader-release') {
      if (this.elector) {
        this.elector.acquire();
      }
      return;
    }

    if (payload.kind === 'message') {
      this.dispatchMessage(payload.message, { fromBroadcast: true });
      return;
    }

    if (payload.kind === 'status') {
      this.dispatchStatus(payload.status);
    }
  }

  broadcast (payload) {
    if (!this.channelSupported || !this.channel || !payload) {
      return;
    }

    this.channel.postMessage({
      ...payload,
      tabId: this.tabId,
      accountScope: this.accountScope
    });
  }

  createFetchWithAuth () {
    return async (input, init = {}) => {
      const nextInit = { ...init };
      const headers = new Headers(nextInit.headers || {});
      const token = getToken();

      if (token) {
        headers.set('Authorization', 'Bearer ' + token);
      } else {
        headers.delete('Authorization');
      }

      nextInit.headers = headers;
      const response = await window.fetch(input, nextInit);
      if (response && response.status >= 500) {
        const error = new Error('SSE transient HTTP ' + response.status);
        error.status = response.status;
        error.statusText = response.statusText || '';
        throw error;
      }
      return response;
    };
  }

  openSSE () {
    if (!this.isStarted || !this.isLeader || this.eventSource) {
      return;
    }

    const currentGeneration = this.reconnectGeneration;
    const connectionId = ++this.connectionId;
    const url = baseURL + '/connect';
    const eventSource = new FetchEventSource(url, {
      fetch: this.createFetchWithAuth(),
      reconnectionDelay: 10000
    });
    this.eventSource = eventSource;

    const isStaleConnection = () => {
      return (
        !this.isStarted ||
        !this.isLeader ||
        this.reconnectGeneration !== currentGeneration ||
        this.connectionId !== connectionId ||
        this.eventSource !== eventSource
      );
    };

    eventSource.onopen = () => {
      if (isStaleConnection()) {
        return;
      }
      this.clearReconnectTimer();
      this.isReconnecting = false;
      this.reconnectAttempt = 0;
      this.retryCount = 0;
      const status = {
        state: 'open',
        electorMode: this.electorMode
      };
      this.dispatchStatus(status);
      this.broadcast({ kind: 'status', status });
    };

    eventSource.onmessage = event => {
      if (isStaleConnection()) {
        return;
      }
      const message = {
        type: event.type || 'message',
        data: event.data,
        lastEventId: event.lastEventId || ''
      };

      this.dispatchMessage(message, { fromBroadcast: false });
      this.broadcast({ kind: 'message', message });
    };

    eventSource.onerror = event => {
      if (isStaleConnection()) {
        return;
      }
      this.retryCount += 1;

      const errorStatus = event && event.error && event.error.status;
      const errorStatusText = event && event.error && event.error.statusText;
      const errorMsg = typeof errorStatus === 'number'
        ? ('SSE transient HTTP ' + errorStatus + (errorStatusText ? (' ' + errorStatusText) : ''))
        : (event && event.message ? event.message : 'SSE connection error');
      const status = {
        state: 'error',
        message: errorMsg,
        retryCount: this.retryCount,
        electorMode: this.electorMode
      };

      this.dispatchStatus(status);
      this.broadcast({ kind: 'status', status });
    };
  }

  closeSSE (targetConnectionId) {
    if (!this.eventSource) {
      return;
    }
    if (typeof targetConnectionId === 'number' && targetConnectionId !== this.connectionId) {
      return;
    }
    const active = this.eventSource;
    this.eventSource = null;
    active.close();
  }

  getBackoffDelay () {
    const cappedAttempt = Number.isFinite(this.maxRetries) && this.maxRetries > 0
      ? Math.min(this.reconnectAttempt, this.maxRetries)
      : this.reconnectAttempt;
    return Math.min(RETRY_BASE_DELAY * Math.pow(2, cappedAttempt), this.maxRetryDelay);
  }

  clearReconnectTimer () {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  resetReconnectController () {
    this.reconnectGeneration++;
    this.clearReconnectTimer();
    this.reconnectAttempt = 0;
    this.retryCount = 0;
    this.isReconnecting = false;
  }

  scheduleReconnect ({ immediate = false } = {}) {
    if (!this.isStarted || !this.isLeader) {
      return;
    }
    if (this.eventSource) {
      return;
    }

    if (immediate) {
      this.clearReconnectTimer();
    } else if (this.reconnectTimer) {
      return;
    }

    this.isReconnecting = true;
    const generation = this.reconnectGeneration;
    const delay = immediate ? 0 : this.getBackoffDelay();
    if (!immediate) {
      this.reconnectAttempt++;
    }

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (generation !== this.reconnectGeneration || !this.isStarted || !this.isLeader || this.eventSource) {
        return;
      }
      this.openSSE();
    }, delay);
  }

  handleOnline () {
    this.isOffline = false;
    if (!this.isStarted || !this.isLeader) {
      return;
    }
    this.scheduleReconnect({ immediate: true });
  }

  handleOffline () {
    this.isOffline = true;
  }

  parseBusinessPayload (message) {
    if (!message || typeof message !== 'object') {
      return null;
    }

    if (Object.prototype.hasOwnProperty.call(message, 'payload')) {
      return message.payload;
    }

    if (typeof message.data !== 'string') {
      return null;
    }

    const payload = safeParse(message.data);
    return payload && typeof payload === 'object' ? payload : null;
  }

  normalizeContent (content) {
    if (typeof content === 'undefined' || content === null) {
      return content;
    }
    if (typeof content !== 'string') {
      return content;
    }
    const parsed = safeParse(content);
    return parsed && typeof parsed === 'object' ? parsed : content;
  }

  resolveMessageType (payload) {
    if (!payload || typeof payload !== 'object') {
      return '';
    }
    const explicitType = payload.messageType || payload.msgType || payload.typeName;
    if (explicitType) {
      return asMessageTypeKey(explicitType);
    }
    if (typeof payload.type !== 'undefined' && Object.prototype.hasOwnProperty.call(LEGACY_TYPE_MESSAGE_MAP, payload.type)) {
      return asMessageTypeKey(LEGACY_TYPE_MESSAGE_MAP[payload.type]);
    }
    return '';
  }

  resolveNotificationLevel (payload, messageType, content) {
    if (payload && payload.level) {
      return String(payload.level).toLowerCase();
    }
    if (payload && payload.notifyLevel) {
      return String(payload.notifyLevel).toLowerCase();
    }
    if (messageType === 'VOUCHER_GENERATE' && content && typeof content === 'object' && typeof content.status !== 'undefined') {
      return content.status ? 'success' : 'error';
    }
    if (payload && payload.type === 1 && content && typeof content === 'object' && typeof content.status !== 'undefined') {
      return content.status ? 'success' : 'error';
    }
    return 'info';
  }

  resolveNotificationTitle (payload, messageType) {
    if (payload && payload.title) {
      return payload.title;
    }
    if (messageType === 'VOUCHER_GENERATE') {
      return '凭证生成通知';
    }
    return '消息';
  }

  resolveNotificationEventId (message, payload) {
    if (payload && payload.eventId) {
      return String(payload.eventId);
    }
    if (payload && payload.uid) {
      return String(payload.uid);
    }
    if (payload && payload.bizKey) {
      return String(payload.bizKey);
    }
    if (message && message.lastEventId) {
      return String(message.lastEventId);
    }
    return '';
  }

  resolveNotificationCreatedTime (payload) {
    if (payload && payload.createdTime) {
      return payload.createdTime;
    }
    if (payload && payload.createTime) {
      return payload.createTime;
    }
    if (payload && payload.timestamp) {
      return payload.timestamp;
    }
    return new Date().toISOString();
  }

  resolveNotificationAction (payload) {
    if (!payload || typeof payload !== 'object' || !payload.action || typeof payload.action !== 'object') {
      return null;
    }

    const actionType = payload.action.type ? String(payload.action.type).trim().toLowerCase() : '';
    const actionTarget = payload.action.target ? String(payload.action.target).trim() : '';
    if (!actionTarget || (actionType !== 'route' && actionType !== 'url')) {
      return null;
    }

    return {
      type: actionType,
      target: actionTarget,
      query: payload.action.query && typeof payload.action.query === 'object' ? payload.action.query : undefined,
      newTab: payload.action.newTab !== false
    };
  }

  normalizeNotification (message, payload) {
    const resolvedPayload = payload && typeof payload === 'object' ? payload : {};
    const rawContent = Object.prototype.hasOwnProperty.call(resolvedPayload, 'content')
      ? resolvedPayload.content
      : resolvedPayload.contentJson;
    const content = this.normalizeContent(rawContent);
    const messageType = this.resolveMessageType(resolvedPayload) || 'UNKNOWN';
    const level = this.resolveNotificationLevel(resolvedPayload, messageType, content);
    const title = this.resolveNotificationTitle(resolvedPayload, messageType);

    return {
      eventId: this.resolveNotificationEventId(message, resolvedPayload),
      messageType,
      title,
      content: typeof content === 'undefined' ? resolvedPayload : content,
      level,
      createdTime: this.resolveNotificationCreatedTime(resolvedPayload),
      action: this.resolveNotificationAction(resolvedPayload),
      raw: {
        message,
        payload: resolvedPayload
      }
    };
  }

  dispatchMessage (message, meta = {}) {
    const payload = this.parseBusinessPayload(message);
    const notification = this.normalizeNotification(message, payload);

    this.messageSubscribers.forEach(listener => listener(notification, message, payload, meta));

    if (payload && typeof payload.type !== 'undefined') {
      const handlers = this.typeSubscribers.get(asTypeKey(payload.type));
      if (handlers) {
        handlers.forEach(handler => handler(payload, message, meta));
      }
    }

    const messageTypeHandlers = this.messageTypeSubscribers.get(asMessageTypeKey(notification.messageType));
    if (messageTypeHandlers) {
      messageTypeHandlers.forEach(handler => handler(notification, message, payload, meta));
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('finance-global-sse-message', {
        detail: {
          notification,
          message,
          payload,
          meta
        }
      }));
    }
  }

  dispatchStatus (status) {
    this.statusSubscribers.forEach(listener => listener(status));

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('finance-global-sse-status', {
        detail: status
      }));
    }
  }

  handleBeforeUnload () {
    this.resetReconnectController();
    if (this.elector) {
      this.broadcast({
        kind: 'leader-release',
        mode: this.electorMode
      });
      this.elector.release();
    }
    this.closeSSE();
  }
}

const sharedSSEManager = new SharedSSEManager();

export { sharedSSEManager };
